import {Result} from "neverthrow"
import {ErrorDocument, OkDocument} from "./common"
import {
	Application,
	ApplicationDocument,
	ApplicationDocumentSide,
	DocumentLink,
	PatchApplicationDocument,
	approveDocument as approveOnboardingApplicationDocument,
	getDocumentLink as getOnboardingApplicationDocumentLink,
	rejectDocument as rejectOnboardingApplicationDocument,
	updateDocument as updateOnboardingApplicationDocument,
	uploadDocument as uploadOnboardingApplicationDocument,
} from "./application"
import {
	ProductApplication,
	approveDocument as approveProductApplicationDocument,
	getDocumentLink as getProductApplicationDocumentLink,
	rejectDocument as rejectProductApplicationDocument,
	updateDocument as updateProductApplicationDocument,
	uploadDocument as uploadProductApplicationDocument,
} from "./productApplication"

export type DocumentableApplication = Application | ProductApplication

export function isProductApplication(app: DocumentableApplication): app is ProductApplication {
	switch (app.type) {
		case "businessProductApplication":
		case "individualProductApplication":
			return true
		case "businessApplication":
		case "individualApplication":
			return false
	}
}

type DocumentLinkResult = Promise<Result<OkDocument<DocumentLink>, ErrorDocument>>
type DocumentApplicationResult = Promise<Result<OkDocument<DocumentableApplication>, ErrorDocument>>
type DocumentUpdateResult = Promise<Result<OkDocument<ApplicationDocument>, ErrorDocument>>

export function documentableGetDocumentLink(
	app: DocumentableApplication,
	documentId: string,
	accessToken: string,
	side: ApplicationDocumentSide
): DocumentLinkResult {
	return isProductApplication(app)
		? getProductApplicationDocumentLink(app.id, documentId, accessToken, side)
		: getOnboardingApplicationDocumentLink(app.id, documentId, accessToken, side)
}

export function documentableUploadDocument(
	app: DocumentableApplication,
	accessToken: string,
	documentId: string,
	documentFile: any,
	documentType: string | undefined,
	documentSide: ApplicationDocumentSide
): DocumentApplicationResult {
	if (isProductApplication(app)) {
		return uploadProductApplicationDocument(accessToken, app.id, documentId, documentFile, documentType, documentSide)
	}
	return uploadOnboardingApplicationDocument(accessToken, app.id, documentId, documentFile, documentType, documentSide)
}

export function documentableApproveDocument(
	app: DocumentableApplication,
	accessToken: string,
	documentId: string
): DocumentApplicationResult {
	if (isProductApplication(app)) {
		return approveProductApplicationDocument(accessToken, app.id, documentId)
	}
	return approveOnboardingApplicationDocument(accessToken, app.id, documentId)
}

export function documentableRejectDocument(
	app: DocumentableApplication,
	accessToken: string,
	documentId: string,
	reasonCode: string,
	reason: string
): DocumentApplicationResult {
	if (isProductApplication(app)) {
		return rejectProductApplicationDocument(accessToken, app.id, documentId, reasonCode, reason)
	}
	return rejectOnboardingApplicationDocument(accessToken, app.id, documentId, reasonCode, reason)
}

export function documentableUpdateDocument(
	app: DocumentableApplication,
	accessToken: string,
	documentId: string,
	document: PatchApplicationDocument
): DocumentUpdateResult {
	if (isProductApplication(app)) {
		return updateProductApplicationDocument(accessToken, app.id, documentId, document)
	}
	return updateOnboardingApplicationDocument(accessToken, app.id, documentId, document)
}
